import { Grid, Typography, Autocomplete } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SearchHelper } from "../../pages/search/SearchHelper";
import EnforcementAppealSearchState from "../../pages/search/EnforcementAppeal/EnforcementAppealsSearchState";
import "../../pages/search/Search.css";
import { Dayjs } from "dayjs";
import { AppealType, E_AppealLevel } from "@shared/mirage/core/enums";
import { EnforcementAppealsFilter } from "@shared/publicportal/contracts/EnforcementAppealsFilter";
import EnforcementAppealsSimpleSearch from "@publicPortal/pages/search/EnforcementAppeal/EnforcementAppealsSimpleSearch";
import datePickerValueFormatter from "@mirage/components/helpers/datePickerValueFormatter";

interface EnforcementAppealAdvancedSearchProps {
    state: EnforcementAppealSearchState;
    setter: (modifier: Partial<EnforcementAppealsFilter>) => void;
}

const EnforcementAppealsAdvancedSearch = (props: EnforcementAppealAdvancedSearchProps) => {
    const { state, setter } = props;

    return (
        <>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <EnforcementAppealsSimpleSearch state={state} setter={setter} />
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <Grid item xs={12} md={12} xl={12}>
                    <Typography variant="h5">Details</Typography>
                </Grid>
                {getDetailFields(state, setter).map((field) => {
                    return (
                        <Grid item xs={12} md={6} xl={4} key={"e-appeal-details-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "45%" } }}>
                <Grid item xs={12} md={12} xl={12}>
                    <Typography variant="h5">Date Fields</Typography>
                </Grid>
                {getDateFields(state, setter).map((field) => {
                    return (
                        <Grid item xs={12} md={6} xl={6} key={"e-appeal-dates-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
};

const getDetailFields = (state: EnforcementAppealSearchState, setter: (modifier: Partial<EnforcementAppealsFilter>) => void) => {
    return [
        <Autocomplete
            options={Object.values(AppealType)}
            id="enforcement-appeal-appeal-type"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="enforcement-appeal-appeal-type-key"
            size="small"
            value={state.filter.appealType?.length ? state.filter.appealType : null}
            inputValue={state.filter.appealType}
            onInputChange={(event, value) => event && setter({ appealType: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Appeal Type")}
        />,
        <Autocomplete
            options={state.decisionTypes || []}
            id="enforcement-appeal-decision-type"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="enforcement-appeal-decision-type-key"
            size="small"
            value={state.filter.decisionType?.length ? state.filter.decisionType : null}
            inputValue={state.filter.decisionType}
            onInputChange={(event, value) => event && setter({ decisionType: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Decision Type")}
        />,
        <Autocomplete
            options={state.wards || []}
            id="enforcement-appeal-ward"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="enforcement-appeal-ward-key"
            size="small"
            value={state.filter.ward?.length ? state.filter.ward : null}
            inputValue={state.filter.ward}
            onInputChange={(event, value) => event && setter({ ward: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Ward")}
        />,
        <Autocomplete
            options={state.parishes || []}
            id="enforcement-appeal-parish"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="enforcement-appeal-parish-key"
            size="small"
            value={state.filter.parish?.length ? state.filter.parish : null}
            inputValue={state.filter.parish}
            onInputChange={(event, value) => event && setter({ parish: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Parish")}
        />,
        <Autocomplete
            options={Object.values(E_AppealLevel)}
            id="enforcement-appeal-appeal-level"
            className="search-form-autocomplete-field autocomplete-width-18rem"
            key="enforcement-appeal-appeal-level-key"
            size="small"
            value={state.filter.appealLevel?.length ? state.filter.appealLevel : null}
            inputValue={state.filter.appealLevel}
            onInputChange={(event, value) => event && setter({ appealLevel: value })}
            renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Appeal Level")}
        />,
    ];
};

const getDateFields = (state: EnforcementAppealSearchState, setter: (modifier: Partial<EnforcementAppealsFilter>) => void) => {
    return [
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-decision-date-from-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.decisionDateFrom)}
                onChange={(value: Dayjs | null) => setter({ decisionDateFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-decision-date-from") } }}
                label="Appeal Decision Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-decision-date-to-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.decisionDateTo)}
                onChange={(value: Dayjs | null) => setter({ decisionDateTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-decision-date-to") } }}
                label="Appeal Decision Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-claim-expiration-date-from-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.claimExpirationDateFrom)}
                onChange={(value: Dayjs | null) => setter({ claimExpirationDateFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-claim-expiration-date-from") } }}
                label="Claim Expiration Date From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-claim-expiration-date-to-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.claimExpirationDateTo)}
                onChange={(value: Dayjs | null) => setter({ claimExpirationDateTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-claim-expiration-date-to") } }}
                label="Claim Expiration Date To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-date-started-from-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.dateStartedFrom)}
                onChange={(value: Dayjs | null) => setter({ dateStartedFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-date-started-from") } }}
                label="Date Started From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-date-started-to-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.dateStartedTo)}
                onChange={(value: Dayjs | null) => setter({ dateStartedTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-date-started-to") } }}
                label="Date Started To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-date-notified-from-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.dateNotifiedFrom)}
                onChange={(value: Dayjs | null) => setter({ dateNotifiedFrom: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-date-notified-from") } }}
                label="Date Notified From"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
        <LocalizationProvider dateAdapter={AdapterDayjs} key="enforcement-appeal-date-notified-to-key">
            <DatePicker
                value={datePickerValueFormatter(state.filter.dateNotifiedTo)}
                onChange={(value: Dayjs | null) => setter({ dateNotifiedTo: value?.toDate() })}
                slotProps={{ textField: { ...SearchHelper.dateInputProps("enforcement-appeal-date-notified-to") } }}
                label="Date Notified To"
                className="min-width-18rem"
            />
        </LocalizationProvider>,
    ];
};

export default EnforcementAppealsAdvancedSearch;
